import React, { useCallback, useEffect, useRef, useState } from "react";
import JumboContentLayout from "@jumbo/components/JumboContentLayout";
import PagesFiltersToolsBar from "app/pages/components/PagesFiltersToolsBar";
import ProcessList from "app/pages/components/ProcessList/ProcessList";
import EmptyListWarning from "app/pages/components/EmptyListWarning/EmptyListWarning";
import PaginationComponent from "app/pages/components/Pagination";
import Accordion, { accordionClasses } from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails, {
  accordionDetailsClasses,
} from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Fade from "@mui/material/Fade";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import Refresh from "@mui/icons-material/Refresh";
import AccessTime from "@mui/icons-material/AccessTime";
import { useAPI } from "app/hooks/useApi";
import Loading from "app/pages/components/Loading";
import { Snackbar } from "@mui/material";
import { mapApiError } from "app/utils/errorMessages";

const GRID_ITEM_STYLE = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
};

const MarcasProcessos = () => {
  const [count, setCount] = useState(100);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [processos, setProcessos] = useState([]);
  const [RPIs, setRPIs] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);

  const [hasSearched, setHasSearched] = useState(false);

  // Função para exportar todos os dados da consulta para CSV
  const handleExportCSV = async () => {
    setExportingCSV(true);
    try {
      let url = `processos-revistas-rpi?sortBy=${sortBy}&sortOrder=${sortOrder}`;
      if (searchTerm) {
        url += `&searchTerm=${searchTerm}`;
      }
      const {
        marca,
        natureza,
        num_processo,
        tipo_envolvido,
        titular,
        revista,
        processos_sem_procuradores,
        processos_terceiros,
        processos_vivian,
        envolvido,
        responsavel,
        somente_selecionados,
      } = filtersRef.current;
      if (marca) url += `&marca=${marca}`;
      if (natureza) url += `&natureza=${natureza}`;
      if (num_processo) url += `&num_processo=${num_processo}`;
      if (tipo_envolvido) url += `&tipo_envolvido=${tipo_envolvido}`;
      if (titular) url += `&titular=${titular}`;
      if (revista) url += `&rpiNumber=${revista}`;
      if (processos_terceiros) url += `&processosTerceiros=${processos_terceiros}`;
      if (processos_sem_procuradores) url += `&processosSemProcuradores=${processos_sem_procuradores}`;
      if (processos_vivian) url += `&processosVivian=${processos_vivian}`;
      if (envolvido) url += `&envolvido=${envolvido}`;
      if (responsavel) url += `&responsavel=${responsavel}`;
      if (somente_selecionados && confirmedSelections.size > 0) {
        const selectedIds = Array.from(confirmedSelections).join(',');
        url += `&processosIds=${selectedIds}`;
      }
      // Adiciona um parâmetro para buscar todos os dados (ex: perPage=100000)
      url += `&perPage=100000&page=0`;
      const response = await makeRequest(url, "GET");
      if (response.status === 200 && response.data.processos) {
        const allProcessos = response.data.processos;
        if (allProcessos.length === 0) {
          alert("Nenhum dado encontrado para exportar.");
          setExportingCSV(false);
          return;
        }
  // Remove campos indesejados
        const excludeFields = ['createdAt', 'updatedAt'];
        const csvHeaders = Object.keys(allProcessos[0]).filter(h => !excludeFields.includes(h));
        const csvRows = allProcessos.map(obj => csvHeaders.map(h => `"${(obj[h] ?? '').toString().replace(/"/g, '""')}"`).join(','));
        const csvContent = [csvHeaders.join(','), ...csvRows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const urlBlob = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = urlBlob;
        link.setAttribute('download', 'processos_exportados.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(urlBlob);
      } else {
        alert("Erro ao buscar dados para exportação.");
      }
    } catch (err) {
      alert("Erro ao exportar CSV: " + err.message);
    }
    setExportingCSV(false);
  };

  // Estados para seleção e atualização de processos
  const [selectedProcessos, setSelectedProcessos] = useState(new Set());
  const [pendingSelections, setPendingSelections] = useState(new Set());
  const [confirmedSelections, setConfirmedSelections] = useState(new Set());
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const [loadingConfirmacao, setLoadingConfirmacao] = useState(false);

  const [filters, setFilters] = useState({
    num_processo: "",
    tipo_envolvido: "",
    marca: "",
    titular: "",
    natureza: "",
    revista: "",
    processos_terceiros: false,
    processos_sem_procuradores: false,
    processos_vivian: false,
    envolvido: "",
    responsavel: "",
    somente_selecionados: false,
  });
  const filtersRef = useRef(filters);

  const { makeRequest: makeRequestAPI, loading } = useAPI();
  const [errorMsg, setErrorMsg] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  
  
  // Criar uma referência estável para makeRequest para evitar loops infinitos

  // Criar uma referência estável para makeRequest para evitar loops infinitos
  const makeRequestRef = useRef();
  makeRequestRef.current = makeRequestAPI;

  const makeRequest = useCallback((...args) => {
    return makeRequestRef.current(...args);
  }, []);

  const handleExpansion = () => {
    setExpanded((prevExpanded) => !prevExpanded);
  };

  const handleChangeFilters = (key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [`${key}`]: value,
    }));
  };

  const handleCleanFilters = () => {
    setFilters({
      num_processo: "",
      tipo_envolvido: "",
      marca: "",
      titular: "",
      natureza: "",
      revista: "",
      processos_terceiros: false,
      processos_sem_procuradores: false,
      processos_vivian: false,
      envolvido: "",
      responsavel: "",
      somente_selecionados: false,
    });
    // Limpar também as seleções quando limpar filtros
    setSelectedProcessos(new Set());
    setPendingSelections(new Set());
    setConfirmedSelections(new Set());
  };

  const handleSelectProcesso = (processoId) => {
    setSelectedProcessos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(processoId)) {
        newSet.delete(processoId);
      } else {
        newSet.add(processoId);
      }
      return newSet;
    });

    // Atualizar seleções pendentes
    setPendingSelections(prev => {
      const newSet = new Set(prev);
      const wasConfirmed = confirmedSelections.has(processoId);
      const isNowSelected = !selectedProcessos.has(processoId);

      if (wasConfirmed && !isNowSelected) {
        // Era confirmada e agora foi desmarcada - adicionar como pendente
        newSet.add(processoId);
      } else if (!wasConfirmed && isNowSelected) {
        // Não era confirmada e agora foi marcada - adicionar como pendente
        newSet.add(processoId);
      } else {
        // Retornou ao estado original - remover das pendentes
        newSet.delete(processoId);
      }
      
      return newSet;
    });
  };

  // Função para selecionar/deselecionar todos os processos visíveis
  const handleSelectAll = () => {
    const allCurrentIds = processos.map(processo => processo.id);
    const allSelected = allCurrentIds.every(id => selectedProcessos.has(id));
    const newSelected = !allSelected;
    
    setSelectedProcessos(prev => {
      const newSet = new Set(prev);
      if (newSelected) {
        allCurrentIds.forEach(id => newSet.add(id));
      } else {
        allCurrentIds.forEach(id => newSet.delete(id));
      }
      return newSet;
    });

    setPendingSelections(prev => {
      const newSet = new Set(prev);
      allCurrentIds.forEach(id => {
        const wasConfirmed = confirmedSelections.has(id);
        const isNowSelected = newSelected;

        if (wasConfirmed && !isNowSelected) {
          newSet.add(id);
        } else if (!wasConfirmed && isNowSelected) {
          newSet.add(id);
        } else {
          newSet.delete(id);
        }
      });
      return newSet;
    });
  };

  const handleUpdateSelected = async () => {
    if (selectedProcessos.size === 0) {
      alert('Selecione pelo menos um processo para atualizar.');
      return;
    }

    setIsUpdating(true);
    setUpdatingItems(new Set(selectedProcessos));

    try {
      const selectedIds = Array.from(selectedProcessos);
      await new Promise(resolve => setTimeout(resolve, 2000));
      alert(`${selectedIds.length} processo(s) atualizado(s) com sucesso!`);
      setSelectedProcessos(new Set());
      setPendingSelections(new Set());
      setConfirmedSelections(new Set());
    } catch (error) {
      alert('Erro ao atualizar processos no INPI.');
    } finally {
      setIsUpdating(false);
      setUpdatingItems(new Set());
    }
  };

  const handleConfirmarSelecoes = async () => {
    if (pendingSelections.size === 0) {
      alert('Nenhuma alteração pendente para confirmar.');
      return;
    }

    const confirmacao = window.confirm(
      `Deseja confirmar ${pendingSelections.size} alteração(ões) pendente(s)?\n\nEsta ação irá salvar as seleções no sistema.`
    );

    if (!confirmacao) return;

    try {
      setLoadingConfirmacao(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setConfirmedSelections(new Set(selectedProcessos));
      setPendingSelections(new Set());
      alert(`Seleções confirmadas com sucesso! ${selectedProcessos.size} processos selecionados.`);
    } catch (error) {
      alert('Erro ao confirmar seleções. Verifique sua conexão.');
    } finally {
      setLoadingConfirmacao(false);
    }
  };

  const handleCancelarAlteracoes = () => {
    setSelectedProcessos(new Set(confirmedSelections));
    setPendingSelections(new Set());
  };

  const handleClearAllSelections = () => {
    setSelectedProcessos(new Set());
    setPendingSelections(new Set());
    setConfirmedSelections(new Set());
  };

  const getProcessos = useCallback(async () => {
    let url = `processos-revistas-rpi?page=${page}&perPage=${perPage}&sortBy=${sortBy}&sortOrder=${sortOrder}`;

    if (searchTerm) {
      url += `&searchTerm=${searchTerm}`;
    }

    const {
      marca,
      natureza,
      num_processo,
      tipo_envolvido,
      titular,
      revista,
      processos_sem_procuradores,
      processos_terceiros,
      processos_vivian,
      envolvido,
      responsavel,
      somente_selecionados,
    } = filtersRef.current;

    if (marca) url += `&marca=${marca}`;
    if (natureza) url += `&natureza=${natureza}`;
    if (num_processo) url += `&num_processo=${num_processo}`;
    if (tipo_envolvido) url += `&tipo_envolvido=${tipo_envolvido}`;
    if (titular) url += `&titular=${titular}`;
    if (revista) url += `&rpiNumber=${revista}`;
    if (processos_terceiros) url += `&processosTerceiros=${processos_terceiros}`;
    if (processos_sem_procuradores) url += `&processosSemProcuradores=${processos_sem_procuradores}`;
    if (processos_vivian) url += `&processosVivian=${processos_vivian}`;
    if (envolvido) url += `&envolvido=${envolvido}`;
    if (responsavel) url += `&responsavel=${responsavel}`;

    if (somente_selecionados && confirmedSelections.size > 0) {
      const selectedIds = Array.from(confirmedSelections).join(',');
      url += `&processosIds=${selectedIds}`;
    }

    try {
      const response = await makeRequest(url, "GET");
      if (response.status === 200) {
        setProcessos(response.data.processos);
        setCount(response.data.total);
        setErrorMsg("");
      } else {
        setErrorMsg(mapApiError(response));
      }
    } catch (err) {
      setErrorMsg(mapApiError(err));
      setProcessos([]);
      setCount(0);
    }
  }, [page, perPage, sortBy, sortOrder, searchTerm, makeRequest]);

  // Botão "Buscar": marca que houve uma busca e reseta para página 0
  const handleSearch = useCallback(() => {
    setHasSearched(true);
    // Se já estava na página 0, getProcessos precisa ser chamado diretamente
    // pois o useEffect de paginação só reage a mudanças
    if (page === 0) {
      getProcessos();
    } else {
      // Resetar para página 0 vai disparar o useEffect de paginação automaticamente
      setPage(0);
    }
  }, [page, getProcessos]);

  // useEffect de paginação: só re-busca se o usuário já tiver buscado antes
  useEffect(() => {
    if (hasSearched) {
      getProcessos();
    }
  }, [page, perPage, sortBy, sortOrder]);
  // Nota: getProcessos e hasSearched intencionalmente omitidos para evitar
  // disparos duplicados — a paginação só re-executa quando page/perPage/sort mudam.

  const getRPIs = useCallback(async () => {
    try {
      const response = await makeRequest(`/rpis/all`, "GET");
      if (response.status === 200) {
        setRPIs(response.data);
      } else {
        setSnackbar({ open: true, severity: 'error', message: mapApiError(response) });
      }
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: mapApiError(err) });
    }
  }, [makeRequest]);

  // ✅ getRPIs ainda carrega ao montar (lista de revistas para o Select)
  useEffect(() => {
    getRPIs();
  }, [getRPIs]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  return (
    <JumboContentLayout header={<h1>Marcas - Processos</h1>}>
      <PagesFiltersToolsBar
        value={searchTerm}
        setValue={setSearchTerm}
        onSearch={handleSearch}
      />

      <Accordion
        expanded={expanded}
        onChange={handleExpansion}
        slots={{ transition: Fade }}
        slotProps={{ transition: { timeout: 400 } }}
        style={{ margin: "10px 0" }}
        sx={[
          expanded
            ? {
                [`& .${accordionClasses.region}`]: { height: "auto" },
                [`& .${accordionDetailsClasses.root}`]: { display: "block" },
              }
            : {
                [`& .${accordionClasses.region}`]: { height: 0 },
                [`& .${accordionDetailsClasses.root}`]: { display: "none" },
              },
        ]}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
          <Typography component="span">Filtros de busca</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container width="100%" spacing={1} justifyContent={"center"}>
            <Grid item xs={1} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Nº Revista:</Typography>
              <Select
                fullWidth
                size="small"
                value={filters.revista}
                onChange={(event) => handleChangeFilters(`revista`, event.target.value)}
              >
                {RPIs?.length > 0 ? (
                  RPIs.map((rpi, index) => (
                    <MenuItem key={`${rpi.id}-${index}`} value={rpi.numero}>
                      {rpi.numero}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value={null}>Todas</MenuItem>
                )}
              </Select>
            </Grid>
            <Grid item xs={1.5} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Nº Processo:</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.num_processo}
                onChange={(event) => handleChangeFilters(`num_processo`, event.target.value)}
              />
            </Grid>
            <Grid item xs={2} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Tipo de Envolvido:</Typography>
              <Select fullWidth value="" size="small" />
            </Grid>
            <Grid item xs={2.5} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Marca:</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.marca}
                onChange={(event) => handleChangeFilters(`marca`, event.target.value)}
              />
            </Grid>
            <Grid item xs={2.5} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Titular:</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.titular}
                onChange={(event) => handleChangeFilters(`titular`, event.target.value)}
              />
            </Grid>
            <Grid item xs={2.5} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Natureza:</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.natureza}
                onChange={(event) => handleChangeFilters(`natureza`, event.target.value)}
              />
            </Grid>
          </Grid>

          <Grid container width="100%" spacing={1} justifyContent={"flex-start"} my={1}>
            <Grid item xs={2.5} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Envolvido:</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.envolvido}
                onChange={(event) => handleChangeFilters(`envolvido`, event.target.value)}
              />
            </Grid>
            <Grid item xs={2.5} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Responsável:</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.responsavel}
                onChange={(event) => handleChangeFilters(`responsavel`, event.target.value)}
              />
            </Grid>
            <Grid item xs={2.5} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Processos de terceiros:</Typography>
              <Checkbox
                size="small"
                checked={filters.processos_terceiros}
                onChange={(event) => handleChangeFilters(`processos_terceiros`, event.target.checked)}
              />
            </Grid>
            <Grid item xs={2.5} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Processos sem procuradores:</Typography>
              <Checkbox
                size="small"
                checked={filters.processos_sem_procuradores}
                onChange={(event) => handleChangeFilters(`processos_sem_procuradores`, event.target.checked)}
              />
            </Grid>
            <Grid item xs={2.5} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Processos da Vivian:</Typography>
              <Checkbox
                size="small"
                checked={filters.processos_vivian}
                onChange={(event) => handleChangeFilters(`processos_vivian`, event.target.checked)}
              />
            </Grid>
            <Grid item xs={2.5} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Somente Selecionados:</Typography>
              <Checkbox
                size="small"
                checked={filters.somente_selecionados}
                onChange={(event) => handleChangeFilters(`somente_selecionados`, event.target.checked)}
                disabled={confirmedSelections.size === 0}
              />
            </Grid>
          </Grid>

          <Stack direction={"row"} gap={2} width="100%" justifyContent={"flex-end"} my={1} p={1}>
            {/* ✅ Botão Buscar agora chama handleSearch que controla o fluxo */}
            <Button
              variant="contained"
              color="info"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              Buscar
            </Button>
            <Button
              variant="outlined"
              color="success"
              onClick={handleExportCSV}
              disabled={exportingCSV}
            >
              {exportingCSV ? 'Exportando...' : 'Exportar CSV'}
            </Button>
            <Button
              variant="outlined"
              color="info"
              startIcon={<FilterAltOffIcon />}
              onClick={handleCleanFilters}
            >
              Limpar
            </Button>
            
            
            {/* Botões de seleção quando há processos carregados */}

            {/* Botões de seleção quando há processos carregados */}
            {processos.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSelectAll}
                  disabled={isUpdating}
                >
                  {processos.every(p => selectedProcessos.has(p.id)) ? 'Desmarcar Todos' : 'Selecionar Todos'}
                </Button>

                {selectedProcessos.size > 0 && (
                  <>
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ alignSelf: 'center', fontWeight: 'bold' }}
                    >
                      {selectedProcessos.size} selecionado(s)
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleUpdateSelected}
                      disabled={isUpdating}
                      startIcon={isUpdating ? <AccessTime /> : <Refresh />}
                      sx={{
                        backgroundColor: '#4CAF50',
                        '&:hover': { backgroundColor: '#45a049' }
                      }}
                    >
                      {isUpdating ? 'Atualizando...' : 'Atualizar no INPI'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleClearAllSelections}
                      disabled={isUpdating}
                      color="warning"
                    >
                      Limpar Seleções
                    </Button>
                  </>
                )}
              </>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Card de Confirmação de Seleções */}
      {(selectedProcessos.size > 0 || pendingSelections.size > 0) && (
        <Card
          sx={{
            textAlign: "left",
            padding: 2,
            marginBottom: 2,
            border: pendingSelections.size > 0 ? '2px solid #ff9800' : '2px solid #4CAF50',
            backgroundColor: pendingSelections.size > 0 ? 'rgba(255, 152, 0, 0.05)' : 'rgba(76, 175, 80, 0.05)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: pendingSelections.size > 0 ? '#ff9800' : '#4CAF50', fontWeight: 'bold' }}>
              📋 Processos Selecionados ({selectedProcessos.size})
              {pendingSelections.size > 0 && (
                <Typography component="span" sx={{ color: '#ff9800', fontSize: '0.9em', ml: 1 }}>
                  • {pendingSelections.size} alteração(ões) pendente(s)
                </Typography>
              )}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {pendingSelections.size > 0 && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="medium"
                  onClick={handleCancelarAlteracoes}
                  sx={{
                    minWidth: 100,
                    borderColor: '#ff9800',
                    color: '#ff9800',
                    '&:hover': { borderColor: '#f57c00', backgroundColor: 'rgba(255, 152, 0, 0.04)' }
                  }}
                >
                  Cancelar
                </Button>
              )}
              <Button
                variant="contained"
                color={pendingSelections.size > 0 ? "warning" : "success"}
                size="medium"
                disabled={loadingConfirmacao || pendingSelections.size === 0}
                onClick={handleConfirmarSelecoes}
                startIcon={loadingConfirmacao ? <CircularProgress size={18} /> : null}
                sx={{
                  minWidth: 140,
                  backgroundColor: pendingSelections.size > 0 ? '#ff9800' : '#4CAF50',
                  '&:hover': { backgroundColor: pendingSelections.size > 0 ? '#f57c00' : '#45a049' },
                  '&:disabled': { backgroundColor: '#ccc' }
                }}
              >
                {loadingConfirmacao ? 'Confirmando...' :
                  pendingSelections.size > 0 ? 'Confirmar Alterações' : 'Nenhuma Alteração'}
              </Button>
            </Box>
          </Box>

          {pendingSelections.size > 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Você tem {pendingSelections.size} alteração(ões) pendente(s).
              Clique em "Confirmar Alterações" para salvar no sistema ou "Cancelar" para reverter.
            </Alert>
          ) : (
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Seleções confirmadas no sistema. Use o filtro "Somente Selecionados" para ver apenas estes processos.
            </Typography>
          )}

          {/* Seção de ações quando há seleções confirmadas */}
          {selectedProcessos.size > 0 && pendingSelections.size === 0 && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2, p: 2, backgroundColor: 'rgba(33, 150, 243, 0.05)', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: '#1976D2', fontWeight: 'bold', flex: 1 }}>
                🔄 Ações para {selectedProcessos.size} processo(s) confirmado(s):
              </Typography>
              
              <Button
                variant="contained"
                color="info"
                size="medium"
                disabled={isUpdating}
                onClick={handleUpdateSelected}
                startIcon={isUpdating ? <AccessTime /> : <Refresh />}
                sx={{
                  minWidth: 160,
                  backgroundColor: '#2196F3',
                  '&:hover': { backgroundColor: '#1976D2' },
                  '&:disabled': { backgroundColor: '#ccc' }
                }}
              >
                {isUpdating ? 'Atualizando...' : 'Atualizar no INPI'}
              </Button>

              <Button
                variant="outlined"
                size="medium"
                onClick={handleClearAllSelections}
                disabled={isUpdating}
                color="warning"
                sx={{
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  '&:hover': { borderColor: '#f57c00', backgroundColor: 'rgba(255, 152, 0, 0.04)' }
                }}
              >
                Limpar Todas
              </Button>
            </Box>
          )}
        </Card>
      )}

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
      )}

      {!hasSearched ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Use os filtros acima e clique em <strong>Buscar</strong> para carregar os processos.
        </Alert>
      ) : loading ? (
        <Loading />
      ) : processos && processos.length > 0 ? (
        <ProcessList
          processos={processos}
          selectedProcessos={selectedProcessos}
          pendingSelections={pendingSelections}
          confirmedSelections={confirmedSelections}
          onSelectProcesso={handleSelectProcesso}
          updatingItems={updatingItems}
          showSelectionControls={true}
        />
      ) : (
        <EmptyListWarning />
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {hasSearched && (
        <PaginationComponent
          count={count}
          page={page}
          perPage={perPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          setPage={setPage}
          setPerPage={setPerPage}
          setSortBy={setSortBy}
          setSortOrder={setSortOrder}
        />
      )}
    </JumboContentLayout>
  );
};

export default React.memo(MarcasProcessos);